(function vimeoPlayer(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.VideoPlayers = global.NewHomeSource.VideoPlayers || {};

  class Vimeo {
    /**
     * Initializes a new instance of the Vimeo player handler.
     */
    constructor() {
      this.vimeoApi = '//player.vimeo.com/api/player.js';
      this.playerInformation = { };
      this.defaultEvents = {
        onEnded: function onEnded() { },
        onPlaying: function onPlaying() { },
        onPaused: function onPaused() { },
        onLoaded: function onLoaded() { },
      };
      this.eventsHandler = {};
      this.player = null;
    }

    /**
     * Initializes the Vimeo video player with the given source and configuration.
     *
     * @param {string} source - The video ID or source identifier for the Vimeo player.
     * @param {Object} config - Configuration object to customize event handlers and player settings.
     */
    initialize(source, config) {
      this.videoId = source;
      this.eventsHandler = Object.assign({ }, this.defaultEvents, config);
    }

    /**
    * Returns the path where the JavaScript for the requested player can be found.
    */
    apiFilepath() {
      return global.document.location.protocol + this.vimeoApi;
    }

    /**
    * Triggers the onPlaying event handler.
    */
    playingHandler(data) {
      const eventData = data;
      eventData.videoId = this.videoId;
      document.body.dispatchEvent(new CustomEvent('videoInteraction', {
        detail: { originator: 'vimeo', interaction: 'video_play' }
      }));
      this.eventsHandler.onPlaying(eventData);
    }

    /**
     * Triggers the onPaused event handler.
     */
    pausedHandler(data) {
      const eventData = data;
      eventData.videoId = this.videoId;
      // Only send pause event if video is not ended.
      if (data.duration > data.seconds && data.seconds > 0) {
        document.body.dispatchEvent(new CustomEvent('videoInteraction', {
          detail: { originator: 'vimeo', interaction: 'video_pause' }
        }));
      }
      this.eventsHandler.onPaused(eventData);
    }

    /**
     * Triggers the onEnded event handler.
     */
    endedHandler(data) {
      const eventData = data;
      eventData.videoId = this.videoId;
      document.body.dispatchEvent(new CustomEvent('videoInteraction', {
        detail: { originator: 'vimeo', interaction: 'video_completed' }
      }));
      this.eventsHandler.onEnded(eventData);
    }

    /**
     * Event handler called when the Vimeo player is ready.
     * If the `playOnLoaded` flag is set in `eventsHandler`, the video will start playing automatically.
     */
    onReady() {
      if (this.eventsHandler.playOnLoaded) {
        this.player.play();
      }
    }

    /**
     * Creates a new Vimeo Player.
     */
    async createVideo() {
      await this.loadVimeoScript();
      const options = {
        id: this.videoId,
      };

      if (this.player) {
        this.player.destroy();
      }

      this.player = new global.Vimeo.Player(this.videoId, options);
      this.player.on('playing', this.playingHandler.bind(this));
      this.player.on('pause', this.pausedHandler.bind(this));
      this.player.on('ended', this.endedHandler.bind(this));
      await new Promise((resolve) => {
        this.player.on('loaded', () => {
          this.onReady();
          resolve();
        });
      });
    }

    /**
    * Loads the Vimeo JavaScript needed for the player.
    */
    loadVimeoScript() {
      return new Promise((resolve) => {
        if (typeof global.Vimeo === 'object') {
          resolve();
          return;
        }

        if (global.define) {
          require(
            ['vimeoPlayer'],
            (vimeoPlayerCallback) => {
              global.Vimeo = {};
              global.Vimeo.Player = vimeoPlayerCallback;
              resolve();
            }
          );

          return;
        }

        const tag = document.getElementsByTagName('meta')[0];
        const script = document.createElement('script');

        script.async = true;
        script.type = 'text/javascript';
        script.src = this.apiFilepath();
        script.onload = resolve;
        tag.parentNode.insertBefore(script, tag);
      });
    }

    /**
    * Destroy the video reference for vimeo.
    *
    */
    dispose() {
      if (!this.player) {
        return;
      }
      this.player.destroy();
    }

    /**
    * Pauses the vimeo video.
    *
    */
    pause() {
      if (!this.player) {
        return;
      }
      this.player.pause();
    }

    /**
    * Plays the vimeo video.
    *
    */
    play() {
      if (!this.player) {
        return;
      }
      this.player.play();
    }

    /**
    * Creates an HTML element needed by the Vimeo JavaScript to create the player.
    *
    */
    static createContainer(id) {
      const videoElement = document.createElement('div');
      videoElement.setAttribute('responsive', 'true');
      videoElement.classList.add('nhs-n1-c-video--vimeo');
      videoElement.id = id;
      videoElement.innerHTML = '';
      return videoElement;
    }
  }

  global.NewHomeSource.VideoPlayers.Vimeo = Vimeo;
}(window));
